import React from 'react';
import { Box, Tooltip, Chip } from '@mui/material';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EmergencyIcon from '@mui/icons-material/Emergency';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import WorkIcon from '@mui/icons-material/Work';
import EuroIcon from '@mui/icons-material/Euro';
import { useTranslation } from 'react-i18next';

const EMERGENCY_CONFIG = {
  normal: { color: 'var(--rd-emergency-normal)', bg: 'var(--rd-emergency-normal-bg)', icon: CheckCircleOutlineIcon },
  urgent: { color: 'var(--rd-emergency-urgent)', bg: 'var(--rd-emergency-urgent-bg)', icon: PriorityHighIcon },
  emergency: { color: 'var(--rd-emergency-emergency)', bg: 'var(--rd-emergency-emergency-bg)', icon: EmergencyIcon },
};

const INSURANCE_CONFIG = {
  pkv: { icon: SecurityIcon, labelKey: 'report.insuranceStatus.pkv', color: 'var(--rd-insurance)', bg: 'var(--rd-insurance-bg)' },
  gkv: { icon: PublicIcon, labelKey: 'report.insuranceStatus.gkv', color: 'var(--rd-insurance)', bg: 'var(--rd-insurance-bg)' },
  bg: { icon: WorkIcon, labelKey: 'report.insuranceStatus.bg', color: 'var(--rd-insurance)', bg: 'var(--rd-insurance-bg)' },
  sz: { icon: EuroIcon, labelKey: 'report.insuranceStatus.sz', color: 'var(--rd-insurance)', bg: 'var(--rd-insurance-bg)' },
};

function IconBadge({ label, color, bgColor, Icon }) {
  return (
    <Tooltip title={label} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          color,
          backgroundColor: bgColor,
          cursor: 'default',
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
    </Tooltip>
  );
}

/**
 * Chip mit Icon + Text im Stil des Report Editors.
 * Wird z.B. in den Task-Tabellen (MyTasks/TeamTasks) als Spalten-Renderer genutzt.
 */
function StatusChip({ Icon, label, color, bgColor }) {
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 15 }} />}
      label={label}
      sx={{
        height: 24,
        fontWeight: 600,
        color,
        backgroundColor: bgColor,
        '& .MuiChip-icon': { color, marginLeft: '6px' },
      }}
    />
  );
}

export function InsuranceStatusChip({ value }) {
  const { t } = useTranslation();
  const cfg = INSURANCE_CONFIG[value];
  if (!cfg) return null;
  return <StatusChip Icon={cfg.icon} label={t(cfg.labelKey)} color={cfg.color} bgColor={cfg.bg} />;
}

export function EmergencyStatusChip({ value }) {
  const { t } = useTranslation();
  const cfg = EMERGENCY_CONFIG[value];
  if (!cfg) return null;
  return <StatusChip Icon={cfg.icon} label={t(`report.emergencyStatus.${value}`)} color={cfg.color} bgColor={cfg.bg} />;
}

export default function StatusBadges({ insuranceStatus, emergencyStatus }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
      {insuranceStatus && INSURANCE_CONFIG[insuranceStatus] && (() => {
        const cfg = INSURANCE_CONFIG[insuranceStatus];
        return (
          <IconBadge
            label={t(cfg.labelKey)}
            color={cfg.color}
            bgColor={cfg.bg}
            Icon={cfg.icon}
          />
        );
      })()}
      {emergencyStatus && EMERGENCY_CONFIG[emergencyStatus] && (() => {
        const cfg = EMERGENCY_CONFIG[emergencyStatus];
        return (
          <IconBadge
            label={t(`report.emergencyStatus.${emergencyStatus}`)}
            color={cfg.color}
            bgColor={cfg.bg}
            Icon={cfg.icon}
          />
        );
      })()}
    </Box>
  );
}
